import React, { useCallback, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import lodash from 'lodash';
import { Button, Skeleton } from '@gamesb42/ui-kit';

import useActivationOrder, { AoTypeEnum } from 'hooks/api/useActivationOrder';
import { getActivationOrderTitle } from 'helpers/formatters';
import MenuItemAo from 'components/MenuItemAo';
import Title from 'components/common/Title';
import urls from 'constants/urls';
import getAccountsByOrderId from 'queries/getAccountsByOrderId';
import { Account } from 'types/ActivationOrderTypes';

import Calculations from './Calculations';
import General from './General';
import Transactions from './Transactions';
import AuditLog from './AuditLog';
import styles from './aoEdit.module.scss';

interface AOEditProps {
  setFixLayoutType: () => void;
  setFlexibleLimitType: () => void;
}

const AOEdit = ({ setFixLayoutType, setFlexibleLimitType }: AOEditProps) => {
  const { t } = useTranslation();
  const {
    getActivationOrder,
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    getActivationOrderIsFetching,
    activationOrderData,
    deleteActivationOrder,
    clearActivationOrderData,
    getScheduleForFutureReceivables,
    clearScheduleForFutureReceivables,
  } = useActivationOrder();
  const [accounts, setAccounts] = useState<Account[]>([]);
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const { activationOrderId } = useParams();
  const history = useHistory();
  const type = activationOrderData?.ao_type;
  const [order, setOrder] = useState(0);
  const isUseQueryType = type === AoTypeEnum.FUTURE_RECEIVABLES || type === AoTypeEnum.AVAILABLE_RECEIVABLES;
  const [status, setStatus] = useState();
  const [loadError, setLoadError] = useState<string | null>(null);
  const isDataEmpty = lodash.isEmpty(activationOrderData);

  const title = getActivationOrderTitle(activationOrderData);

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const updateAccounts = (id) =>
    getAccountsByOrderId(id)
      .then(setAccounts)
      // eslint-disable-next-line no-console
      .catch((error) => console.error('Error loading activationOrderId', error));

  const fetchActivationOrder = useCallback(() => {
    if (!activationOrderId) return;

    setLoadError(null);
    getActivationOrder({
      id: activationOrderId,
      type: isUseQueryType ? type : undefined,
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      errorCallback: (error) => {
        setLoadError(error?.body?.description || error?.text || t('forms.activationOrder.loadError'));
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activationOrderId, isUseQueryType]);

  useEffect(() => {
    fetchActivationOrder();
    updateAccounts(activationOrderId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activationOrderId, isUseQueryType]);

  useEffect(
    () => () => {
      clearActivationOrderData();
    },
    [],
  );

  useEffect(() => {
    if (type === AoTypeEnum.FUTURE_RECEIVABLES) {
      getScheduleForFutureReceivables({ id: activationOrderId });
    }

    return () => {
      clearScheduleForFutureReceivables();
    };
  }, [type, activationOrderId]);

  useEffect(() => {
    if (activationOrderData) setStatus(activationOrderData.status);
  }, [activationOrderData]);

  if (loadError) {
    return (
      <div className={styles.errorWrapper}>
        <div className={styles.errorMessage}>{loadError}</div>
        <div className={styles.loadingRow}>
          <Button type="primary" onClick={fetchActivationOrder}>
            {t('forms.activationOrder.retry')}
          </Button>
          <Button onClick={() => history.push(urls.getActivationOrder())}>{t('global.cancelC')}</Button>
        </div>
      </div>
    );
  }

  if (isDataEmpty && getActivationOrderIsFetching) {
    return (
      <div className={styles.loadingWrapper}>
        <Skeleton type="text16" width="320px" />
        <div className={styles.loadingRow}>
          <Skeleton type="text14" width="180px" />
          <Skeleton type="text14" width="180px" />
        </div>
        <Skeleton type="image" />
        <div className={styles.loadingRow}>
          <Skeleton type="text14" width="240px" />
          <Skeleton type="text14" width="240px" />
        </div>
      </div>
    );
  }

  return (
    <>
      <div style={{ marginBottom: 20 }}>
        {!lodash.isEmpty(activationOrderData) && (
          <Title
            clickCallback={() => {
              history.push(urls.getActivationOrder());
            }}
            text={title}
            aoType={type}
            isShowBackButton
            status={status}
          />
        )}
        <MenuItemAo
          setValue={(value) => {
            // console.log('type, value -->', type, value);

            // if (type === AoTypeEnum.AVAILABLE_RECEIVABLES && value === 1) return;

            if (type === AoTypeEnum.FUTURE_RECEIVABLES) return;

            value === 2 ? setFlexibleLimitType() : setFixLayoutType();
            setOrder(value);
          }}
          order={order}
          styleTabs={{ marginTop: 33, marginBottom: 20 }}
          items={
            type === AoTypeEnum.CLASSIC
              ? ['General', 'Transaction', 'Calculations', 'Audit log']
              : ['General', 'Transaction', 'Audit log']
          }
        />
      </div>
      {order === 0 && !lodash.isEmpty(activationOrderData) && (
        <General
          type={type}
          activationOrderData={activationOrderData}
          deleteActivationOrder={deleteActivationOrder}
          title={title}
          accounts={accounts}
          updateAccounts={updateAccounts}
          setStatus={setStatus}
        />
      )}
      {order === 1 && (
        <Transactions
          type={type}
          activationOrderData={activationOrderData}
          deleteActivationOrder={deleteActivationOrder}
          title={title}
          setStatus={setStatus}
        />
      )}
      {type === AoTypeEnum.CLASSIC && order === 2 && <Calculations />}
      {type !== AoTypeEnum.CLASSIC && order === 2 && (
        <AuditLog
          type={type}
          activationOrderData={activationOrderData}
          deleteActivationOrder={deleteActivationOrder}
          title={title}
          setStatus={setStatus}
        />
      )}
      {type === AoTypeEnum.CLASSIC && order === 3 && (
        <AuditLog
          type={type}
          activationOrderData={activationOrderData}
          deleteActivationOrder={deleteActivationOrder}
          title={title}
          setStatus={setStatus}
        />
      )}
    </>
  );
};

export default AOEdit;
